import { useState } from "react";
import { Check } from "lucide-react";
import { AreaField, Field, TextField } from "@/components/admin/fields";
import { LIEUX } from "@/components/site/nav";
import { HoursField } from "@/components/admin/HoursField";
import { PhotoField } from "@/components/admin/PhotoField";

// several categories per place; the first one ticked is the main one (marker colour)
const CategoryPicker = ({ value, onChange }) => {
  const toggle = (k) => onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  return (
    <div className="flex flex-wrap gap-2" data-testid="place-form-categories">
      {LIEUX.map((l) => {
        const on = value.includes(l.key);
        return (
          <button key={l.key} type="button" onClick={() => toggle(l.key)} aria-pressed={on} data-testid={`place-form-category-${l.key}`}
            className={`inline-flex items-center gap-2 px-4 h-11 border text-sm font-bold uppercase tracking-[0.1em] transition-colors ${on ? "bg-white text-black border-white" : "border-white/30 text-white hover:border-white"}`}>
            {on && <Check className="w-4 h-4" />}{l.label}{on && value[0] === l.key && value.length > 1 && <span className="text-[10px] text-primary">principale</span>}
          </button>
        );
      })}
    </div>
  );
};

export const PlaceForm = ({ initial, onSubmit, submitting }) => {
  const [f, setF] = useState(() => {
    const base = { name: "", address: "", city: "", country: "France", description: "", ...initial };
    return { ...base, categories: initial?.categories?.length ? initial.categories : [initial?.category || "disquaires"] };
  });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    const { name, categories, address, city, country, description, image, hours } = f;
    const cleanHours = Object.fromEntries(Object.entries(hours || {}).filter(([, s]) => s?.length));
    onSubmit({ name, categories, address: address?.trim() || null, city, country, description, image: image || null,
      hours: Object.keys(cleanHours).length ? cleanHours : null });
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="place-form">
      <TextField label="Nom *" value={f.name} onChange={set("name")} required testId="place-form-name" />
      <Field label="Catégories * (plusieurs possibles)">
        <CategoryPicker value={f.categories} onChange={set("categories")} />
        {f.categories.length === 0 && <p className="text-sm text-red-400 mt-2" data-testid="place-form-categories-error">Choisis au moins une catégorie.</p>}
      </Field>
      <TextField label="Adresse (pour placer le lieu sur la carte)" value={f.address || ""} onChange={set("address")} placeholder="ex : 9 rue de la Paix" testId="place-form-address" />
      <div className="grid sm:grid-cols-2 gap-5">
        <TextField label="Ville *" value={f.city} onChange={set("city")} required testId="place-form-city" />
        <TextField label="Pays *" value={f.country} onChange={set("country")} required testId="place-form-country" />
      </div>
      <AreaField label="Description * (adresse, spécialités…)" value={f.description} onChange={set("description")} rows={4} testId="place-form-description" />
      <Field label="Photo du lieu">
        <PhotoField value={f.image || ""} onChange={set("image")} testId="place-form-photo" />
      </Field>
      <Field label="Horaires d'ouverture (facultatif)">
        <HoursField value={f.hours} onChange={set("hours")} />
      </Field>
      <button type="submit" disabled={submitting || !f.description.trim() || !f.categories.length} data-testid="place-form-submit" className="bg-primary text-white font-display uppercase text-lg px-8 py-4 hover:bg-white hover:text-black transition-colors duration-200 disabled:opacity-50">
        Enregistrer
      </button>
    </form>
  );
};
